import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { fmtUSD, relTime } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Trash2, ArrowUp, ArrowDown } from "lucide-react";

export default function Alerts() {
  const [assets, setAssets] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [form, setForm] = useState({ symbol: "BTC", asset_type: "crypto", target_price: "", direction: "above" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      await api.post("/alerts/check");
    } catch (e) {
      void e;
    }
    const [{ data: a }, { data: n }, { data: al }] = await Promise.all([
      api.get("/market/assets"),
      api.get("/notifications"),
      api.get("/alerts"),
    ]);
    setAssets(a);
    setNotifications(n);
    setAlerts(al);
  };

  useEffect(() => { load().catch(() => {}); }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/alerts", { ...form, target_price: Number(form.target_price) });
      toast.success("Alert created");
      setForm({ ...form, target_price: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const del = async (id) => {
    await api.delete(`/alerts/${id}`);
    toast.success("Alert removed");
    load();
  };

  return (
    <div className="space-y-8" data-testid="alerts-root">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Alerts</p>
        <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tight mt-2">Never miss a move</h1>
        <p className="text-gray-500 mt-2">Set price triggers on stocks & crypto. We evaluate them every time you open TradePilot.</p>
      </div>

      <div className="grid lg:grid-cols-12 gap-8">
        <Card className="lg:col-span-5 border border-gray-200 rounded-2xl p-6">
          <p className="font-display font-semibold text-lg">Create alert</p>
          <form onSubmit={create} className="mt-4 space-y-4" data-testid="alert-form">
            <div>
              <Label className="text-xs uppercase tracking-[0.1em] font-bold text-gray-500">Asset</Label>
              <Select
                value={`${form.asset_type}:${form.symbol}`}
                onValueChange={(v) => {
                  const [t, s] = v.split(":");
                  setForm({ ...form, asset_type: t, symbol: s });
                }}
              >
                <SelectTrigger data-testid="alert-asset" className="h-11 rounded-xl border-gray-200 mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-96">
                  {assets.map((a) => (
                    <SelectItem key={`${a.asset_type}:${a.symbol}`} value={`${a.asset_type}:${a.symbol}`}>
                      <span className="font-medium mr-2">{a.symbol}</span>
                      <span className="text-gray-500 text-xs">{a.name}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-[0.1em] font-bold text-gray-500">Direction</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <button
                    type="button"
                    data-testid="alert-dir-above"
                    onClick={() => setForm({ ...form, direction: "above" })}
                    className={`rounded-full py-2.5 text-xs font-medium ${
                      form.direction === "above" ? "bg-gain text-white" : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    <ArrowUp size={12} className="inline mr-1" /> Above
                  </button>
                  <button
                    type="button"
                    data-testid="alert-dir-below"
                    onClick={() => setForm({ ...form, direction: "below" })}
                    className={`rounded-full py-2.5 text-xs font-medium ${
                      form.direction === "below" ? "bg-loss text-white" : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    <ArrowDown size={12} className="inline mr-1" /> Below
                  </button>
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.1em] font-bold text-gray-500">Target price</Label>
                <Input
                  data-testid="alert-price"
                  type="number"
                  step="any"
                  min="0"
                  required
                  value={form.target_price}
                  onChange={(e) => setForm({ ...form, target_price: e.target.value })}
                  className="h-11 rounded-xl bg-gray-50 border-gray-200 focus:ring-brand focus:border-brand mt-2 font-mono"
                  placeholder="0.00"
                />
              </div>
            </div>
            <Button
              data-testid="alert-submit"
              type="submit"
              disabled={busy}
              className="w-full h-11 rounded-full bg-brand text-white hover:bg-brand-deep font-medium"
            >
              Create alert
            </Button>
          </form>
        </Card>

        <Card className="lg:col-span-7 border border-gray-200 rounded-2xl p-6" data-testid="active-alerts">
          <p className="font-display font-semibold text-lg mb-4">Your alerts</p>
          {alerts.length === 0 ? (
            <p className="text-sm text-gray-500">No alerts yet.</p>
          ) : (
            <div className="divide-y divide-gray-100">
              {alerts.map((a) => (
                <div key={a._id} className="flex items-center justify-between py-3" data-testid={`alert-row-${a._id}`}>
                  <div className="flex items-center gap-3">
                    {a.direction === "above" ? (
                      <ArrowUp className="text-gain" size={16} />
                    ) : (
                      <ArrowDown className="text-loss" size={16} />
                    )}
                    <div>
                      <p className="font-medium text-sm">
                        {a.symbol} {a.direction} <span className="font-mono">{fmtUSD(a.target_price)}</span>
                      </p>
                      <p className="text-xs text-gray-500 capitalize">
                        {a.asset_type} · Status: <span className={a.status === "triggered" ? "text-gain font-medium" : ""}>{a.status}</span>
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => del(a._id)}
                    data-testid={`alert-delete-${a._id}`}
                    className="p-2 rounded-full hover:bg-gray-100 text-gray-400 hover:text-loss"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <Card className="border border-gray-200 rounded-2xl p-6" data-testid="notifications-list">
        <p className="font-display font-semibold text-lg mb-4">Notification history</p>
        {notifications.length === 0 ? (
          <p className="text-sm text-gray-500">No notifications yet.</p>
        ) : (
          <div className="divide-y divide-gray-100">
            {notifications.map((n) => (
              <div key={n._id} className="py-3">
                <p className="text-sm font-medium">{n.title}</p>
                <p className="text-xs text-gray-600 mt-0.5">{n.body}</p>
                <p className="text-[10px] text-gray-400 mt-1">{relTime(n.created_at)}</p>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
